import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Loader2, ArrowLeft, FolderPlus, FileText, FileSpreadsheet, ExternalLink, Lock, Search as SearchIcon } from "lucide-react";

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function Companies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPaid = user?.is_paid;
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [loadingActive, setLoadingActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");

  const load = () => api.get("/companies").then(({ data }) => { setRows(data.companies); setTotal(data.total_companies); }).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const openCompany = async (name) => {
    setLoadingActive(true);
    try { const { data } = await api.get(`/companies/contacts?name=${encodeURIComponent(name)}`); setActive(data); }
    catch { toast.error("Could not load company"); } finally { setLoadingActive(false); }
  };

  const saveAllToList = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data: list } = await api.post("/lists", { name: active.company });
      const { data } = await api.post(`/lists/${list.id}/items`, { contact_ids: active.contacts.map((c) => c.id) });
      toast.success(`Saved ${data.added} to list "${active.company}"`);
    } catch { toast.error("Could not build list"); } finally { setSaving(false); }
  };

  const exportCompany = async (fmt) => {
    if (!isPaid) { toast.error("Downloads are a Pro feature."); navigate("/billing"); return; }
    setExporting(fmt);
    try {
      const items = active.contacts.map((c) => ({ id: c.id, reason: "", match: "Medium" }));
      const { data } = await api.post(`/export/${fmt}`, { items, query: `Company: ${active.company}`, include_reasoning: false, scope: "filtered" }, { responseType: "blob" });
      downloadBlob(data, fmt === "pdf" ? `${active.company}.pdf` : `${active.company}.xlsx`);
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch { toast.error("Export failed"); } finally { setExporting(""); }
  };

  const filtered = rows ? rows.filter((r) => r.company.toLowerCase().includes(q.toLowerCase())) : [];

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
        {!active ? (
          <>
            <div className="mb-6">
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Companies</h1>
              <p className="text-slate-400 mt-1">Who you know at each company. {total > 0 && <span className="font-mono-niq text-slate-500">{total} companies in your network.</span>}</p>
            </div>
            <div className="relative mb-6">
              <SearchIcon className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="company-search" placeholder="Search companies…" className="w-full bg-[#111726] border border-[#27334D] rounded-lg pl-10 pr-3 py-2.5 text-sm outline-none focus:border-blue-500" />
            </div>
            {rows === null ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading companies…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-500 rounded-2xl border border-[#1E2A3F] bg-[#111726]">No companies found. Upload your connections first.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {filtered.map((r) => (
                  <button key={r.company} onClick={() => openCompany(r.company)} data-testid={`company-row-${r.company}`} className="group flex items-center justify-between gap-3 rounded-xl border border-[#27334D] bg-[#111726] p-4 hover:border-blue-500/40 transition-colors text-left">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-blue-400" /></div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{r.company}</div>
                        <div className="text-xs text-slate-500 font-mono-niq truncate">{r.top_sector}</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold text-blue-300 font-mono-niq">{r.count}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-niq">you know</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button onClick={() => setActive(null)} data-testid="company-back" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-5"><ArrowLeft className="w-4 h-4" /> All companies</button>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center shrink-0"><Building2 className="w-6 h-6 text-blue-400" /></div>
                <div className="min-w-0">
                  <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight truncate" data-testid="active-company-name">{active.company}</h1>
                  <p className="text-slate-500 text-sm font-mono-niq">{active.count} contact{active.count !== 1 ? "s" : ""} you know here</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={saveAllToList} disabled={saving} data-testid="company-save-list" className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 px-3 py-2 rounded-lg font-medium transition-colors">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5" />} Save all to list</button>
                <button onClick={() => exportCompany("pdf")} disabled={!!exporting} data-testid="company-export-pdf" className="flex items-center gap-1.5 text-xs border border-[#27334D] hover:border-slate-500 px-3 py-2 rounded-lg transition-colors">{!isPaid ? <Lock className="w-3.5 h-3.5 text-slate-500" /> : exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 text-red-400" />} PDF</button>
                <button onClick={() => exportCompany("excel")} disabled={!!exporting} data-testid="company-export-excel" className="flex items-center gap-1.5 text-xs border border-[#27334D] hover:border-slate-500 px-3 py-2 rounded-lg transition-colors">{!isPaid ? <Lock className="w-3.5 h-3.5 text-slate-500" /> : exporting === "excel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />} Excel</button>
              </div>
            </div>
            {loadingActive ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : (
              <div className="space-y-3">
                {active.contacts.map((c) => (
                  <div key={c.id} className="rounded-xl border border-[#27334D] bg-[#111726] p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-600/15 border border-blue-500/25 flex items-center justify-center text-sm font-semibold text-blue-300 shrink-0">{(c.first_name || "?")[0]}</div>
                    <div className="min-w-0">
                      {c.profile_url ? (
                        <a href={c.profile_url} target="_blank" rel="noopener noreferrer" className="font-medium inline-flex items-center gap-1.5 text-slate-100 hover:text-blue-400 transition-colors group">{c.first_name} {c.last_name}<ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" /></a>
                      ) : (<div className="font-medium truncate">{c.first_name} {c.last_name}</div>)}
                      <div className="text-xs text-slate-500 truncate">{c.position}</div>
                      <div className="flex flex-wrap gap-2 mt-1 text-[11px] text-slate-500 font-mono-niq">{c.seniority && <span>{c.seniority}</span>}{c.region && <span>· {c.region}</span>}{c.email && <span className="text-slate-400">· {c.email}</span>}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
